import React, { useMemo } from "react";

/* Shared renderers for guide topics AND interview tips.
   Do NOT change styling — this is the master template. */

const isPositiveBullet = (text) => /^\s*(✅|🟢|🔵|🟠|🟣|✔️|✔)/.test(text);
const isNegativeBullet = (text) => /^\s*(❌|⚠️|⚠)/.test(text);
const isMyth = (text) => /^\s*❌/.test(text);
const isReality = (text) => /^\s*✅/.test(text);

export const ParagraphBlock = ({ text, extraTop = false }) => (
  <p className={`text-slate-700 leading-[1.75] text-[15.5px] ${extraTop ? "mt-4" : ""}`}>{text}</p>
);

export const H3 = ({ children, extraTop = true }) => (
  <h3 className={`text-[17px] font-semibold text-slate-900 tracking-tight ${extraTop ? "mt-6" : ""} mb-1`}>
    {children}
  </h3>
);

export const H2 = ({ children }) => (
  <h2 className="text-[22px] md:text-[24px] font-semibold text-slate-900 tracking-tight">{children}</h2>
);

export const BulletList = ({ items }) => (
  <ul className="space-y-2 my-2">
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-3">
        <span
          className={`mt-2 shrink-0 rounded-full ${
            isPositiveBullet(t) ? "bg-[#15803d]" : isNegativeBullet(t) ? "bg-[#dc2626]" : "bg-[#1d4ed8]"
          }`}
          style={{ width: 6, height: 6 }}
        />
        <span className="text-slate-700 leading-[1.7] text-[15px]">{t}</span>
      </li>
    ))}
  </ul>
);

export const EthicalPrinciplesTable = ({ rows }) => {
  const body = rows.slice(1);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {body.map((r, i) => (
        <div key={i} className="rounded-xl bg-white/70 border border-[#c1e9d0] p-4">
          <p className="font-semibold text-slate-900">{r[0]}</p>
          <p className="mt-1 text-[14.5px] text-slate-700 leading-relaxed">{r[1]}</p>
        </div>
      ))}
    </div>
  );
};

export const GenericTable = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  const [head, ...body] = rows;
  const isTwoCol = head.length === 2;
  if (isTwoCol) {
    return (
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
        <div className="grid grid-cols-[minmax(160px,1fr)_2fr] bg-[#f5f8fd] text-[13px] font-semibold text-slate-800">
          <div className="px-4 py-2.5 border-b border-slate-200">{head[0]}</div>
          <div className="px-4 py-2.5 border-b border-slate-200 border-l">{head[1]}</div>
        </div>
        {body.map((r, i) => (
          <div key={i} className="grid grid-cols-[minmax(160px,1fr)_2fr] text-[14.5px]">
            <div className="px-4 py-3 border-b border-slate-100 font-medium text-slate-800">{r[0]}</div>
            <div className="px-4 py-3 border-b border-slate-100 border-l text-slate-700 leading-relaxed">{r[1]}</div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white">
      <table className="min-w-full text-[14.5px]">
        <thead className="bg-[#f5f8fd]">
          <tr>
            {head.map((h, i) => (
              <th key={i} className="px-4 py-2.5 text-left font-semibold text-slate-800 border-b border-slate-200">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i} className="border-b border-slate-100">
              {r.map((c, j) => (
                <td key={j} className="px-4 py-3 text-slate-700">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const SectionBody = ({ blocks, sectionTitle }) => {
  const isPitfalls = (sectionTitle || "").toLowerCase().includes("pitfall");
  const rendered = [];
  let listBuffer = [];
  let prevWasReality = false;

  const flushList = (key) => {
    if (!listBuffer.length) return;
    rendered.push(<BulletList key={`ul-${key}`} items={listBuffer} />);
    listBuffer = [];
  };

  blocks.forEach((b, i) => {
    if (b.kind === "list_item") {
      listBuffer.push(b.text);
      return;
    }
    flushList(i);
    if (b.kind === "heading" && b.level === 3) {
      rendered.push(<H3 key={i}>{b.text}</H3>);
      prevWasReality = false;
    } else if (b.kind === "paragraph") {
      const startsWithMyth = isMyth(b.text);
      const extraTop = isPitfalls && startsWithMyth && prevWasReality;
      rendered.push(<ParagraphBlock key={i} text={b.text} extraTop={extraTop} />);
      if (isReality(b.text)) prevWasReality = true;
      else if (startsWithMyth) prevWasReality = false;
    } else if (b.kind === "table") {
      if (b.subtype === "ethical_principles") {
        rendered.push(<EthicalPrinciplesTable key={i} rows={b.rows} />);
      } else {
        rendered.push(
          <div key={i} className="my-3">
            <GenericTable rows={b.rows} />
          </div>
        );
      }
      prevWasReality = false;
    }
  });
  flushList("end");
  return <div className="space-y-2.5">{rendered}</div>;
};

export const useSections = (blocks) =>
  useMemo(() => {
    if (!blocks) return [];
    const sections = [];
    let current = { title: null, blocks: [] };
    blocks.forEach((b) => {
      if (b.kind === "heading" && b.level === 2) {
        if (current.title || current.blocks.length) sections.push(current);
        current = { title: b.text, blocks: [] };
      } else {
        current.blocks.push(b);
      }
    });
    if (current.title || current.blocks.length) sections.push(current);
    return sections;
  }, [blocks]);

/* Card styling per known section title.
   Applies across Study Resources AND Interview Tips. */
export const sectionCard = (title) => {
  const t = (title || "").toLowerCase();
  if (t.includes("topic introduction") || t === "introduction")
    return "bg-gradient-to-br from-[#eef4ff] to-white border-[#dbe6ff]";
  if (t.includes("pitfall")) return "bg-[#fff7ed] border-[#fed7aa]";
  if (t.includes("ethical principles")) return "bg-[#f0fdf4] border-[#bbf7d0]";
  if (t.includes("what to avoid")) return "bg-[#fef2f2] border-[#fecaca]";
  if (t.includes("medily tip")) return "bg-[#fefce8] border-[#fde68a]";
  return "bg-white border-slate-100";
};
